import React, { useEffect, useState } from 'react';
import { Typography, DatePicker, Select, Button, Table, Space, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Medicine } from '../../../services/medicineService';
import api from '../../../services/api';
import ReportLayout from '../ReportLayout';
import '../report-common.css'; // CSS chung cho tất cả báo cáo

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const columns = [
  { title: 'Tên thuốc', dataIndex: 'medicine_name', key: 'medicine_name' },
  { title: 'Đơn vị', dataIndex: 'unit', key: 'unit', width: 80 },
  { title: 'Tồn đầu kỳ', dataIndex: 'ton_dau_ky', key: 'ton_dau_ky', align: 'right' as const, render: (v: number) => v.toLocaleString('vi-VN') },
  { title: 'Nhập trong kỳ', dataIndex: 'nhap_trong_ky', key: 'nhap_trong_ky', align: 'right' as const, render: (v: number) => v.toLocaleString('vi-VN') },
  { title: 'Xuất trong kỳ', dataIndex: 'xuat_trong_ky', key: 'xuat_trong_ky', align: 'right' as const, render: (v: number) => v.toLocaleString('vi-VN') },
  { title: 'Tồn cuối kỳ', dataIndex: 'ton_cuoi_ky', key: 'ton_cuoi_ky', align: 'right' as const, render: (v: number) => v.toLocaleString('vi-VN') },
];

const StockInOutReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [selectedMedicine, setSelectedMedicine] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100']
  });

  useEffect(() => {
    // Lấy danh sách thuốc cho bộ lọc
    api.get('/api/medicines').then(res => {
      setMedicines(res.data.data || []);
    });
  }, []);

  const fetchReport = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params: any = {
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        page,
        limit: pageSize
      };
      if (selectedMedicine) params.medicine_id = selectedMedicine;
      const res = await api.get('/api/reports/nxt', { params });
      
      setData(res.data.data || []);
      if (res.data.pagination) {
        setPagination(prev => ({
          ...prev,
          current: res.data.pagination.page,
          pageSize: res.data.pagination.limit,
          total: res.data.pagination.total
        }));
      }
    } catch (err) {
      message.error('Không thể lấy dữ liệu báo cáo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(1, pagination.pageSize);
    // eslint-disable-next-line
  }, [dateRange, selectedMedicine]);

  const handleTableChange = (paginationConfig: any) => {
    fetchReport(paginationConfig.current, paginationConfig.pageSize);
  };

  // Sidebar bộ lọc
  const sidebar = (
    <>
      <Title level={4} style={{ margin: 0 }}>Chọn khoảng thời gian</Title>
      <div style={{ margin: '16px 0' }}>
        <RangePicker
          value={dateRange}
          onChange={v => v && setDateRange(v as [Dayjs, Dayjs])}
          format="DD/MM/YYYY"
          allowClear={false}
          style={{ width: '100%' }}
        />
      </div>
      <Select
        allowClear
        showSearch
        style={{ width: '100%', marginBottom: 12 }}
        placeholder="Chọn thuốc (hoặc tất cả)"
        value={selectedMedicine}
        onChange={v => setSelectedMedicine(v)}
        optionFilterProp="children"
      >
        {medicines.map(med => (
          <Option key={med.id} value={med.id.toString()}>
            {med.name} ({med.code})
          </Option>
        ))}
      </Select>
      <Button type="primary" onClick={() => fetchReport(1, pagination.pageSize)} loading={loading} block>
        Xem báo cáo
      </Button>
    </>
  );

  return (
    <ReportLayout sidebar={sidebar}>
      <div className="reportContainer">
        <div className="reportHeader">
          <h1 className="reportTitle">BÁO CÁO NHẬP XUẤT TỒN KHO</h1>
          <div className="reportSubtitle">
            Kỳ báo cáo: {dateRange[0].format('DD/MM/YYYY')} - {dateRange[1].format('DD/MM/YYYY')}
          </div>
        </div>
        <div className="reportTable">
          <div className="tableWrapper">
            <Table
              columns={columns}
              dataSource={data}
              rowKey={r => r.medicine_id}
              loading={loading}
              bordered
              pagination={pagination}
              onChange={handleTableChange}
              summary={pageData => {
                if (!pageData.length) return null;
                const sum = (key: string) => pageData.reduce((acc, cur) => acc + (cur[key] || 0), 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><b>Tổng</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2} align="right"><b>{sum('ton_dau_ky').toLocaleString('vi-VN')}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><b>{sum('nhap_trong_ky').toLocaleString('vi-VN')}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right"><b>{sum('xuat_trong_ky').toLocaleString('vi-VN')}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right"><b>{sum('ton_cuoi_ky').toLocaleString('vi-VN')}</b></Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </div>
        </div>
      </div>
    </ReportLayout>
  );
};

export default StockInOutReport; 